'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

interface CalendarViewProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export function CalendarView({
  selectedDate,
  onDateChange
}: CalendarViewProps) {
  const [currentMonth, setCurrentMonth] = useState(selectedDate);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowMonthPicker(false);
        setShowYearPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const months = [
    "1월", "2월", "3월", "4월", "5월", "6월",
    "7월", "8월", "9월", "10월", "11월", "12월"
  ];

  const goToPreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentMonth(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentMonth(newDate);
  };

  const goToMonth = (monthIndex: number) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(monthIndex);
    setCurrentMonth(newDate);
    setShowMonthPicker(false);
  };

  const goToYear = (year: number) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setShowYearPicker(false);
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  };

  const formatSelectedDate = (date: Date) => {
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    return `${date.getMonth() + 1}월 ${date.getDate()}일 ${isToday ? '(오늘)' : ''}`;
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="text-center">
          <div className="text-sm text-gray-600 flex items-center justify-center gap-2">
            {/* 연도 드롭다운 */}
            <div className="relative" ref={containerRef}>
              <button
                onClick={() => {
                  setShowYearPicker(!showYearPicker);
                  setShowMonthPicker(false);
                }}
                className="flex items-center gap-1 hover:bg-gray-100 px-2 py-1 rounded transition-colors"
              >
                {currentMonth.getFullYear()}년
                <ChevronDown className="w-3 h-3" />
              </button>

              {showYearPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-[80px]">
                  <div className="max-h-32 overflow-y-auto py-1">
                    {generateYears().map((year) => (
                      <button
                        key={year}
                        onClick={() => goToYear(year)}
                        className={`w-full px-3 py-1 text-left text-sm hover:bg-gray-100 transition-colors ${
                          currentMonth.getFullYear() === year ? 'bg-blue-500 text-white' : ''
                        }`}
                      >
                        {year}년
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 월 드롭다운 */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowMonthPicker(!showMonthPicker);
                  setShowYearPicker(false);
                }}
                className="flex items-center gap-1 hover:bg-gray-100 px-2 py-1 rounded transition-colors"
              >
                {months[currentMonth.getMonth()]}
                <ChevronDown className="w-3 h-3" />
              </button>

              {showMonthPicker && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-[60px]">
                  <div className="max-h-48 overflow-y-auto py-1">
                    {months.map((month, index) => (
                      <button
                        key={month}
                        onClick={() => goToMonth(index)}
                        className={`w-full px-3 py-1 text-left text-sm hover:bg-gray-100 transition-colors ${
                          currentMonth.getMonth() === index ? 'bg-blue-500 text-white' : ''
                        }`}
                      >
                        {month}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="font-medium text-blue-600">
            {formatSelectedDate(selectedDate)}
          </div>
        </div>

        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={(date) => {
          if (date) {
            onDateChange(date);
            setCurrentMonth(date);
          }
        }}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        className="w-full border-none"
        classNames={{
          months: "flex w-full",
          month: "space-y-2 w-full",
          caption: "hidden",
          table: "w-full border-collapse",
          head_row: "flex mb-1",
          head_cell: "text-gray-500 text-xs w-full text-center p-1 font-medium",
          row: "flex w-full mt-1",
          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 flex-1",
          day: "h-10 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-gray-100 rounded text-sm flex items-center justify-center",
          day_selected: "!bg-blue-500 !text-white hover:!bg-blue-600 !important",
          day_today: "bg-blue-100 text-blue-900",
          day_outside: "text-gray-300",
          day_disabled: "text-gray-300",
        }}
      />
    </div>
  );
}
